import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Filter, ChevronRight, Trash2, RefreshCw } from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input, Select } from '../components/ui/Input.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';
import { SegmentBuilder } from '../components/segments/SegmentBuilder.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Segment {
  id: string;
  name: string;
  description: string | null;
  type: 'dynamic' | 'static';
  definitionJson: Record<string, unknown>;
  memberCount?: number;
  updatedAt: string;
  createdAt: string;
}

interface SegmentForm {
  name: string;
  description: string;
  type: 'dynamic' | 'static';
  definition: Record<string, unknown>;
}

const DEFAULT_FORM: SegmentForm = {
  name: '',
  description: '',
  type: 'dynamic',
  definition: {},
};

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function SegmentsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  const [showModal, setShowModal] = useState(false);
  const [editSegment, setEditSegment] = useState<Segment | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Segment | null>(null);
  const [form, setForm] = useState<SegmentForm>(DEFAULT_FORM);
  const [typeFilter, setTypeFilter] = useState<'all' | 'dynamic' | 'static'>('all');

  const { data = [], isLoading } = useQuery<Segment[]>({
    queryKey: ['segments'],
    queryFn: () => api.get('/api/v1/segments').then((r) => r.data),
  });

  const filtered = typeFilter === 'all' ? data : data.filter((s) => s.type === typeFilter);

  // ── modals ───────────────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditSegment(null);
    setForm(DEFAULT_FORM);
    setShowModal(true);
  };

  const openEdit = (s: Segment) => {
    setEditSegment(s);
    setForm({
      name: s.name,
      description: s.description ?? '',
      type: s.type,
      definition: s.definitionJson ?? {},
    });
    setShowModal(true);
  };

  // ── mutations ────────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (data: SegmentForm) =>
      api.post('/api/v1/segments', { ...data }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['segments'] });
      setShowModal(false);
      toast.success('Segment created');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to create segment')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SegmentForm> }) =>
      api.patch(`/api/v1/segments/${id}`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['segments'] });
      setShowModal(false);
      toast.success('Segment updated');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to update segment')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/segments/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['segments'] });
      setDeleteTarget(null);
      toast.success('Segment archived');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to archive segment')),
  });

  const recomputeMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/segments/${id}/recompute`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['segments'] });
      toast.info('Recomputation started');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to recompute')),
  });

  const handleSubmit = () => {
    if (!form.name) return;
    if (editSegment) {
      updateMutation.mutate({ id: editSegment.id, data: form });
    } else {
      createMutation.mutate(form);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div>
      <PageHeader
        title="Segments"
        description="Audience segments used to target campaigns and automations"
        action={
          <Button size="sm" onClick={openCreate}>
            + New segment
          </Button>
        }
      />

      {/* Type filter tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-zinc-100 pb-0">
        {(['all', 'dynamic', 'static'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTypeFilter(t)}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors capitalize ${
              typeFilter === t
                ? 'border-zinc-900 text-zinc-900'
                : 'border-transparent text-zinc-400 hover:text-zinc-700'
            }`}
          >
            {t}
            <span
              className={`ml-1.5 text-xs tabular-nums ${typeFilter === t ? 'text-zinc-500' : 'text-zinc-300'}`}
            >
              {t === 'all' ? data.length : data.filter((s) => s.type === t).length}
            </span>
          </button>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100">
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Type
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Members
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Created
              </th>
              <th className="px-4 py-3 w-32" />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-50">
            {isLoading ? (
              <SkeletonRows count={4} cols={5} />
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <EmptyState
                    icon={<Filter size={26} />}
                    title={typeFilter !== 'all' ? `No ${typeFilter} segments` : 'No segments yet'}
                    description="Segments group customers for targeted campaigns and automation triggers."
                    action={
                      typeFilter === 'all'
                        ? { label: '+ New segment', onClick: openCreate }
                        : undefined
                    }
                  />
                </td>
              </tr>
            ) : (
              filtered.map((s) => (
                <tr
                  key={s.id}
                  className="hover:bg-zinc-50/60 transition-colors group cursor-pointer"
                  onClick={() => navigate(`/segments/${s.id}`)}
                >
                  <td className="px-4 py-3">
                    <p className="font-medium text-zinc-900">{s.name}</p>
                    {s.description && (
                      <p className="text-xs text-zinc-400 mt-0.5 truncate max-w-[260px]">
                        {s.description}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={s.type === 'dynamic' ? 'info' : 'default'}>{s.type}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-semibold text-zinc-900 tabular-nums">
                      {(s.memberCount ?? 0).toLocaleString()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500">{formatDate(s.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          recomputeMutation.mutate(s.id);
                        }}
                        className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md transition-colors"
                        title="Recompute members"
                      >
                        <RefreshCw
                          size={12}
                          className={recomputeMutation.isPending ? 'animate-spin' : ''}
                        />
                      </button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit(s);
                        }}
                      >
                        Edit
                      </Button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(s);
                        }}
                        className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                        title="Archive segment"
                      >
                        <Trash2 size={12} />
                      </button>
                      <ChevronRight size={13} className="text-zinc-300" />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create / Edit Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editSegment ? `Edit: ${editSegment.name}` : 'New Segment'}
        description={
          editSegment
            ? 'Update name, description, or definition.'
            : 'Define an audience segment to target with campaigns and automations.'
        }
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="VIP customers"
          />
          <Input
            label="Description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Customers with Gold or Platinum tier"
          />
          <Select
            label="Type"
            value={form.type}
            onChange={(e) =>
              setForm((f) => ({ ...f, type: e.target.value as 'dynamic' | 'static' }))
            }
          >
            <option value="dynamic">Dynamic — recomputed automatically</option>
            <option value="static">Static — manually managed membership</option>
          </Select>

          {/* Segment Builder — only for dynamic segments */}
          {form.type === 'dynamic' && (
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-zinc-700">Conditions</label>
              <div className="border border-zinc-200 rounded-xl p-4 bg-zinc-50/40">
                <SegmentBuilder
                  value={form.definition}
                  onChange={(dsl) => setForm((f) => ({ ...f, definition: dsl }))}
                />
              </div>
              <p className="text-xs text-zinc-400">Empty conditions = match all customers</p>
            </div>
          )}

          <ModalFooter>
            <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button size="sm" loading={isPending} disabled={!form.name} onClick={handleSubmit}>
              {editSegment ? 'Save changes' : 'Create segment'}
            </Button>
          </ModalFooter>
        </div>
      </Modal>

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Archive segment?"
        message={`"${deleteTarget?.name}" will be archived and removed from targeting. Members will not be affected.`}
        confirmLabel="Archive"
        variant="warning"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
